import { AppButton, AppIconButton } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Popover, PopoverContent, PopoverTrigger } from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { History, Search, Trash2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useClearAllSearchQueries } from '@/hooks/saved-search/useClearAllSearchQueries'
import { useDeleteSearchQuery } from '@/hooks/saved-search/useDeleteSearchQuery'
import { useSavedSearchQueries } from '@/hooks/saved-search/useSavedSearchQueries'
import type { SavedSearchConfig, SavedSearchQuery } from '@/lib/saved-search'

interface AppSavedSearchQueriesProps {
  config: SavedSearchConfig
  onSelect: (query: string) => void
}

export function AppSavedSearchQueries({ config, onSelect }: AppSavedSearchQueriesProps) {
  const [open, setOpen] = useState(false)

  // 保存済みクエリを取得
  const { data: savedQueries = [], isLoading } = useSavedSearchQueries(config)
  const deleteSearchQuery = useDeleteSearchQuery(config)
  const clearAllSearchQueries = useClearAllSearchQueries(config)

  // クエリを選択
  const handleSelect = (query: SavedSearchQuery) => {
    onSelect(query.query)
    setOpen(false)
  }

  // キーボードでの選択処理
  const handleKeyDown = (event: React.KeyboardEvent, query: SavedSearchQuery) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect(query)
    }
  }

  // クエリを削除
  const handleDelete = (queryName: string, event: React.MouseEvent) => {
    event.stopPropagation()
    deleteSearchQuery.mutate(queryName)
  }

  // すべてのクエリを削除
  const handleClearAll = () => {
    clearAllSearchQueries.mutate()
  }

  if (isLoading || savedQueries.length === 0) {
    return null
  }

  // TODO: <button> が使用されている
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <AppButton variant='outline'>
          <History className='h-4 w-4' />
          保存済み検索 ({savedQueries.length})
        </AppButton>
      </PopoverTrigger>
      <PopoverContent className='w-96 p-0' align='start'>
        <div className='flex items-center justify-between border-b px-4 py-3'>
          <h3 className='font-semibold text-sm'>保存済み検索クエリ</h3>
          {savedQueries.length > 0 && (
            <AppButton variant='ghost' size='sm' onClick={handleClearAll} className='h-auto p-1 text-xs'>
              すべて削除
            </AppButton>
          )}
        </div>

        <div className='max-h-100 overflow-y-auto'>
          {savedQueries.map((query) => (
            <div key={query.name} className='flex items-center border-b hover:bg-muted/50 transition-colors'>
              <button
                type='button'
                className='flex-1 min-w-0 px-4 py-3 text-left cursor-pointer'
                onClick={() => handleSelect(query)}
                onKeyDown={(e) => handleKeyDown(e, query)}
              >
                <div className='flex items-center gap-2'>
                  <Search className='h-3 w-3 text-muted-foreground shrink-0' />
                  <span className='font-medium text-sm truncate'>{query.name}</span>
                </div>
                <p className='text-xs text-muted-foreground mt-1 truncate'>{query.query}</p>
                <p className='text-xs text-muted-foreground mt-1'>
                  {DateTime.fromMillis(query.createdAt).toFormat('yyyy年M月d日 HH:mm')}
                </p>
              </button>
              <div className='px-2'>
                <AppIconButton onClick={(e) => handleDelete(query.name, e)} description='削除'>
                  <Trash2 className='text-destructive' />
                </AppIconButton>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
